/**
 * 作业管理 · AI 生成作业单(Jarod 2026-08-27:"能否直接生成作业,
 * 就不再需要上传文件了 —— 或者上传也保留,让老师选")。
 *
 * 上传那条路原样保留(隔壁的 FolderPanel),这里只是多一个入口:
 * 填主题 → AI 出题 → 老师过目 → 存成作业单。存的时候走的是和手工
 * 上传完全相同的 documents 路径,所以产物落进同一个文件夹,预览、
 * 入库、删除都不用另写一套。
 *
 * 2026-09-15 对齐考试逻辑(用户:"考试可以 customized,作业也要"):
 *   - 快速模式 = 原来的样子:主题 + 题量,范围/难度后端按本班已讲自动取。
 *   - 自定义模式 = 考试蓝图的那一套:已讲知识点点击剔除 / 补充未讲的,
 *     再给题型配比(合计 = 题量)。
 *   - 生成后像试卷页:勾选若干题 + 一句修订要求 → 重出所选 / 删除所选,
 *     其余题原样保留(POST …/generate/revise)。
 *
 * 难度不让老师填 —— 后端按本班的学科分层(高级/中级/基础各几人)
 * 和已讲单元自动定标,和课堂诊断同一套心智模型。
 */
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders, useClassWithRoster, useHomeworkScope, useKnowledgePoints, useSubjects } from "../../api/school";
import { uploadDoc } from "../teachingPlans/documentsApi";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";
import { renderHomeworkHtml, renderHomeworkNamedHtml, type HomeworkSheet } from "./homeworkSheet";
import { openHtmlForPdf } from "../../lib/printHtml";
import { safeSvg } from "../../lib/safeSvg";
import { MathText } from "../../components/MathText";

const ITEM_TYPES = ["mcq", "cloze", "short_answer", "translation", "writing"] as const;
type ItemType = (typeof ITEM_TYPES)[number];
type Mode = "quick" | "custom";

async function postJsonOrThrow<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { detail?: string | { msg?: string }[] };
    const d = b.detail;
    throw new Error(Array.isArray(d) ? d.map((x) => x.msg ?? "").join("; ") : d ?? `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

export function HomeworkGeneratorPanel({
  assignmentId,
  assignmentTitle,
  className,
  assignedDate,
  classId,
  subjectId,
}: {
  /** 这条作业自己的学科;作业单默认按它出(2026-10-13,以前取老师的第一个学科) */
  subjectId?: string | null;
  assignmentId: string;
  assignmentTitle: string;
  className?: string;
  assignedDate?: string;
  classId?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const roster = useClassWithRoster(classId);
  const { employees } = useMyEmployees();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("quick");
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(8);
  const [extra, setExtra] = useState("");
  const [slug, setSlug] = useState("");
  const [sheet, setSheet] = useState<HomeworkSheet | null>(null);
  const [busy, setBusy] = useState<"gen" | "save" | "revise" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // 自定义模式状态(对齐 BlueprintPage:removed / added / matrix)
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [mix, setMix] = useState<Record<ItemType, number>>({ mcq: 3, cloze: 2, short_answer: 3, translation: 0, writing: 0 });

  // 生成后的修订状态(对齐 PaperPage:sel + instruction)
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [instruction, setInstruction] = useState("");

  const subdir = `assignments/${assignmentId}`;
  const subjects = useSubjects();
  const homeworkSubject = subjectId ? (subjects.data ?? []).find((x) => x.id === subjectId) : undefined;
  const effectiveSlug = slug || homeworkSubject?.slug || employees[0]?.slug || "";
  const scope = useHomeworkScope(assignmentId, effectiveSlug || undefined, open && mode === "custom");
  const allKps = useKnowledgePoints("approved", scope.data?.subject_id ?? undefined);
  const taughtIds = useMemo(() => new Set((scope.data?.taught_scope ?? []).map((s) => s.kp_id)), [scope.data]);
  const addableKps = (allKps.data ?? []).filter((k) => k.parent_id !== null && !taughtIds.has(k.id) && !added.has(k.id));
  const mixTotal = ITEM_TYPES.reduce((a, k) => a + (mix[k] || 0), 0);
  const mixOk = mode !== "custom" || mixTotal === count;

  // 换学科/换作业时范围重置,别把上个学科剔除的 id 带过来。
  useEffect(() => {
    setRemoved(new Set());
    setAdded(new Set());
  }, [effectiveSlug, assignmentId]);

  function kpIdsForRequest(): string[] | undefined {
    if (mode !== "custom") return undefined;
    const kept = (scope.data?.taught_scope ?? []).filter((s) => !removed.has(s.kp_id)).map((s) => s.kp_id);
    const ids = [...kept, ...added];
    return ids.length > 0 ? ids : undefined;
  }

  function mixForRequest(): Partial<Record<ItemType, number>> | undefined {
    if (mode !== "custom") return undefined;
    const out: Partial<Record<ItemType, number>> = {};
    for (const k of ITEM_TYPES) if (mix[k] > 0) out[k] = mix[k];
    return out;
  }

  async function generate() {
    setBusy("gen");
    setError(null);
    setSaved(false);
    setSel(new Set());
    try {
      const r = await postJsonOrThrow<HomeworkSheet>(`/api/school/assignments/${assignmentId}/generate`, {
        topic: topic.trim(),
        count,
        subject_slug: effectiveSlug || undefined,
        additional_instructions: extra.trim() || undefined,
        kp_ids: kpIdsForRequest(),
        item_type_counts: mixForRequest(),
      });
      setSheet(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function revise(kind: "regenerate" | "remove") {
    if (!sheet || sel.size === 0) return;
    setBusy("revise");
    setError(null);
    try {
      const r = await postJsonOrThrow<HomeworkSheet>(`/api/school/assignments/${assignmentId}/generate/revise`, {
        sheet,
        regenerate_seqs: kind === "regenerate" ? [...sel] : [],
        remove_seqs: kind === "remove" ? [...sel] : [],
        instruction: instruction.trim() || undefined,
        topic: topic.trim(),
        subject_slug: effectiveSlug || undefined,
        kp_ids: kpIdsForRequest(),
      });
      setSheet(r);
      setSel(new Set());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!sheet) return;
    setBusy("save");
    setError(null);
    try {
      const html = renderHomeworkHtml(sheet, { className, assignedDate });
      // 文件名带主题,老师在文件夹里一眼能认出是哪份;.html 是
      // supernova 认的后缀,入库后可检索。
      const safe = (sheet.title || assignmentTitle).replace(/[\\/:*?"<>|]/g, "_").slice(0, 60);
      await uploadDoc(subdir, `${safe}.html`, new Blob([html], { type: "text/html" }));
      // 记名版(每生一份,卷头已填姓名学号)一起存档,老师直接打印。
      const students = roster.data?.students ?? [];
      if (students.length > 0) {
        const named = renderHomeworkNamedHtml(sheet, { className, assignedDate }, students);
        await uploadDoc(subdir, `${safe}-记名版.html`, new Blob([named], { type: "text/html" }));
      }
      await qc.invalidateQueries({ queryKey: ["documents-tree", subdir] });
      setSaved(true);
      setSheet(null);
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  function toggleSel(seq: number) {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  }

  if (!open) {
    return (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setSaved(false);
          }}
          className="px-3 py-1.5 rounded bg-violet-500 text-white text-sm font-medium hover:bg-violet-600"
        >
          {t("assignments.generate.open")}
        </button>
        <span className="text-xs text-slate-500">{t("assignments.generate.hint")}</span>
        {saved && (
          <span className="text-xs text-emerald-600">{t("assignments.generate.saved")}</span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid="homework-generator">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700">
          {t("assignments.generate.heading")}
        </h4>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSheet(null);
            setError(null);
          }}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          {t("assignments.cancel")}
        </button>
      </div>

      {!sheet && (
        <div className="space-y-2">
          {/* 模式切换 —— 和考试新建表单的「引擎 / 手动」同一款式 */}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded border border-slate-300 overflow-hidden text-xs" role="group">
              {(["quick", "custom"] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  aria-pressed={mode === m}
                  className={`px-2.5 py-1 ${mode === m ? "bg-violet-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {t(`assignments.generate.custom.${m === "quick" ? "modeQuick" : "modeCustom"}`)}
                </button>
              ))}
            </div>
            <span className="text-xs text-slate-500">
              {t(`assignments.generate.custom.${mode === "quick" ? "modeHintQuick" : "modeHintCustom"}`)}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("assignments.generate.topicPlaceholder")}
              className="flex-1 min-w-[200px] px-3 py-1.5 rounded border border-slate-200 text-sm"
            />
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              {t("assignments.generate.count")}
              <input
                type="number"
                min={3}
                max={30}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="w-16 px-2 py-1.5 rounded border border-slate-200 text-sm"
              />
            </label>
            {(employees.length > 1 || (homeworkSubject && !employees.some((e) => e.slug === homeworkSubject.slug))) && (
              <select
                value={effectiveSlug}
                onChange={(e) => setSlug(e.target.value)}
                className="px-2 py-1.5 rounded border border-slate-200 text-sm"
              >
                {homeworkSubject && !employees.some((e) => e.slug === homeworkSubject.slug) && (
                  <option value={homeworkSubject.slug}>{homeworkSubject.name}</option>
                )}
                {employees.map((e) => (
                  <option key={e.subject_id} value={e.slug}>
                    {e.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {mode === "custom" && (
            <div className="space-y-2 rounded border border-violet-200 bg-white p-2.5" data-testid="homework-custom">
              {/* Step 1 — 范围(同 BlueprintPage) */}
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">
                  {t("assignments.generate.custom.scopeHeader", { count: scope.data?.taught_scope.length ?? 0 })}
                </div>
                {scope.isLoading && <p className="text-xs text-slate-400 italic">{t("common.loading")}</p>}
                {scope.data && scope.data.taught_scope.length === 0 && (
                  <p className="text-xs text-amber-600">{t("assignments.generate.custom.noTaught")}</p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {(scope.data?.taught_scope ?? []).map((s) => {
                    const off = removed.has(s.kp_id);
                    return (
                      <button
                        key={s.kp_id}
                        type="button"
                        title={`${s.units.join(" / ")} · ${s.last_taught}`}
                        onClick={() => {
                          const next = new Set(removed);
                          if (off) next.delete(s.kp_id);
                          else next.add(s.kp_id);
                          setRemoved(next);
                        }}
                        className={`rounded border px-2 py-0.5 text-xs ${
                          off ? "border-slate-200 text-slate-300 line-through" : "border-emerald-300 bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {s.name_zh}
                      </button>
                    );
                  })}
                  {[...added].map((id) => {
                    const k = (allKps.data ?? []).find((x) => x.id === id);
                    if (!k) return null;
                    return (
                      <button
                        key={id}
                        type="button"
                        title={t("assignments.generate.custom.addKpWarn")}
                        onClick={() => {
                          const next = new Set(added);
                          next.delete(id);
                          setAdded(next);
                        }}
                        className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700"
                      >
                        {k.name_zh} ⚠︎
                      </button>
                    );
                  })}
                  {addableKps.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) setAdded(new Set([...added, e.target.value]));
                      }}
                      className="rounded border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-600 bg-white"
                      aria-label={t("assignments.generate.custom.addKp")}
                    >
                      <option value="">{t("assignments.generate.custom.addKp")}</option>
                      {addableKps.map((k) => (
                        <option key={k.id} value={k.id}>{k.name_zh}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {/* Step 2 — 题型配比(蓝图矩阵的作业版:只有题型 × 题数) */}
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">{t("assignments.generate.custom.mixHeader")}</div>
                <div className="flex flex-wrap gap-2">
                  {ITEM_TYPES.map((k) => (
                    <label key={k} className="flex items-center gap-1 text-xs text-slate-600">
                      {t(`blueprint.itemTypes.${k === "writing" ? "essay" : k}`, { defaultValue: k })}
                      <input
                        type="number"
                        min={0}
                        max={30}
                        value={mix[k]}
                        onChange={(e) => setMix({ ...mix, [k]: Math.max(0, Number(e.target.value) || 0) })}
                        className="w-14 px-1.5 py-1 rounded border border-slate-200 text-sm"
                        aria-label={k}
                      />
                    </label>
                  ))}
                  <span className={`text-xs self-center ${mixOk ? "text-slate-500" : "text-rose-600"}`}>
                    {t("assignments.generate.custom.mixTotal", { total: mixTotal, count })}
                  </span>
                </div>
              </div>
            </div>
          )}

          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={2}
            placeholder={t("assignments.generate.extraPlaceholder")}
            className="w-full px-3 py-1.5 rounded border border-slate-200 text-sm"
          />
          <button
            type="button"
            disabled={!topic.trim() || busy !== null || !mixOk}
            onClick={generate}
            className="px-3 py-1.5 rounded bg-violet-500 text-white text-sm font-medium hover:bg-violet-600 disabled:opacity-50"
          >
            {busy === "gen"
              ? t("assignments.generate.generating")
              : t("assignments.generate.submit")}
          </button>
        </div>
      )}

      {sheet && (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-slate-800">{sheet.title}</p>
            {sheet.instructions && (
              <p className="text-xs text-slate-500 mt-0.5">{sheet.instructions}</p>
            )}
            {/* 数学组 2026-09-19:「AI 生成题目是否来自题库,还是 AI 自己出题?」—— 作业单不走题库,说清楚。 */}
            <p className="text-[11px] text-slate-400 mt-1">{t("assignments.generate.sourceNote")}</p>
          </div>
          <ol className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {[...sheet.items]
              .sort((a, b) => a.seq - b.seq)
              .map((it) => (
                <li
                  key={it.seq}
                  className={`text-sm bg-white rounded border p-2 flex gap-2 ${sel.has(it.seq) ? "border-violet-400 ring-1 ring-violet-200" : "border-slate-200"}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={sel.has(it.seq)}
                    onChange={() => toggleSel(it.seq)}
                    aria-label={`select ${it.seq}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800">
                      <b className="mr-1">{it.seq}.</b>
                      <MathText text={it.stem} />
                    </div>
                    {safeSvg(it.figure_svg) && (
                      <div
                        className="mt-1 max-w-xs [&>svg]:h-auto [&>svg]:max-w-full"
                        data-testid="homework-figure"
                        dangerouslySetInnerHTML={{ __html: safeSvg(it.figure_svg) as string }}
                      />
                    )}
                    {it.options && it.options.length > 0 && (
                      <ul className="mt-1 text-xs text-slate-600 space-y-0.5 pl-4">
                        {it.options.map((o) => (
                          <li key={o.key}>
                            <b>{o.key}.</b> <MathText text={o.text} />
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-1 text-xs text-emerald-700">
                      {t("assignments.generate.answer")}: <MathText text={it.answer} />
                    </div>
                  </div>
                </li>
              ))}
          </ol>

          {/* 修订工具条 —— 对齐试卷页:已选 n 题 · 修订要求 · 重出所选 · 删除所选 */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">{t("assignments.generate.custom.selected", { n: sel.size })}</span>
            <button type="button" className="text-sky-600 hover:underline" onClick={() => setSel(new Set(sheet.items.map((i) => i.seq)))}>
              {t("assignments.generate.custom.selectAll")}
            </button>
            <button type="button" className="text-sky-600 hover:underline" onClick={() => setSel(new Set())}>
              {t("assignments.generate.custom.clearSel")}
            </button>
            <input
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder={t("assignments.generate.custom.instructionPlaceholder")}
              className="flex-1 min-w-[200px] px-2 py-1 rounded border border-slate-200 text-xs"
            />
            <button
              type="button"
              disabled={busy !== null || sel.size === 0}
              onClick={() => void revise("regenerate")}
              className="px-2.5 py-1 rounded bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
            >
              {busy === "revise" ? t("assignments.generate.custom.revising") : t("assignments.generate.custom.regenerateSel")}
            </button>
            <button
              type="button"
              disabled={busy !== null || sel.size === 0 || sel.size >= sheet.items.length}
              onClick={() => void revise("remove")}
              className="px-2.5 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              {t("assignments.generate.custom.removeSel")}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy !== null || !(roster.data?.students?.length)}
              onClick={() => sheet && openHtmlForPdf(renderHomeworkNamedHtml(sheet, { className, assignedDate }, roster.data?.students ?? []), { print: String(t("documents.preview.printPdf")), hint: String(t("documents.preview.printPdfHint")), title: `${sheet.title}-记名版` })}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              data-testid="homework-print-named"
            >
              {t("assignments.generate.printNamed", { n: roster.data?.students?.length ?? 0 })}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={save}
              className="px-3 py-1.5 rounded bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-50"
            >
              {busy === "save"
                ? t("assignments.generate.saving")
                : t("assignments.generate.save")}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={generate}
              className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-600 hover:bg-white disabled:opacity-50"
            >
              {t("assignments.generate.regenerate")}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
